/* Header behaviours — hook-driven (data-* attributes only, no markup coupling).
 * Lives in an asset (not a {% javascript %} tag) so any fork of a header
 * component inherits the behaviour for free by emitting the same hooks:
 *   data-nav-item / data-nav-trigger          desktop dropdowns & mega-menus
 *   data-drawer / data-drawer-open|close      mobile off-canvas drawer
 *   data-predictive-search|input|results      predictive search
 * Loaded with `defer` from layout/theme.liquid. See contracts/sections/header.md.
 */
(function () {
  /* ── Desktop dropdowns / mega-menus ── */
  const items = document.querySelectorAll('[data-nav-item]');
  const closeAll = (except) => {
    items.forEach((it) => {
      if (it === except) return;
      it.classList.remove('is-open');
      const t = it.querySelector('[data-nav-trigger]');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  };
  const openItem = (it) => {
    closeAll(it);
    it.classList.add('is-open');
    const t = it.querySelector('[data-nav-trigger]');
    if (t) t.setAttribute('aria-expanded', 'true');
  };
  const closeItem = (it) => {
    it.classList.remove('is-open');
    const t = it.querySelector('[data-nav-trigger]');
    if (t) t.setAttribute('aria-expanded', 'false');
  };

  items.forEach((it) => {
    const trigger = it.querySelector('[data-nav-trigger]');
    it.addEventListener('mouseenter', () => openItem(it));
    it.addEventListener('mouseleave', () => {
      if (!it.contains(document.activeElement)) closeItem(it);
    });
    if (trigger) {
      trigger.addEventListener('click', () => {
        it.classList.contains('is-open') ? closeItem(it) : openItem(it);
      });
    }
    it.addEventListener('focusout', (e) => {
      if (!it.contains(e.relatedTarget)) closeItem(it);
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = document.querySelector('[data-nav-item].is-open');
    if (!open) return;
    const t = open.querySelector('[data-nav-trigger]');
    closeItem(open);
    if (t) t.focus();
  });
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-nav-item]')) closeAll(null);
  });

  /* ── Mobile off-canvas drawer ── */
  const drawer = document.querySelector('[data-drawer]');
  const hamburger = document.querySelector('[data-drawer-open]');
  if (drawer && hamburger) {
    const panel = drawer.querySelector('.ax-drawer__panel');
    let lastFocus = null;

    const focusable = () => panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');

    const openDrawer = () => {
      lastFocus = document.activeElement;
      drawer.hidden = false;
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      hamburger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      const close = drawer.querySelector('.ax-drawer__close');
      if (close) close.focus();
    };
    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      hamburger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      const onEnd = () => {
        drawer.hidden = true;
        panel.removeEventListener('transitionend', onEnd);
      };
      panel.addEventListener('transitionend', onEnd);
      if (lastFocus) lastFocus.focus();
    };

    hamburger.addEventListener('click', openDrawer);
    drawer.querySelectorAll('[data-drawer-close]').forEach((b) => b.addEventListener('click', closeDrawer));

    drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeDrawer();
        return;
      }
      if (e.key !== 'Tab') return;
      const f = Array.from(focusable());
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    /* Accordion sub-menus inside the drawer */
    drawer.querySelectorAll('.ax-drawer__toggle').forEach((btn) => {
      btn.addEventListener('click', () => {
        const row = btn.closest('.ax-drawer__row');
        const sub = row ? row.nextElementSibling : null;
        if (!sub) return;
        const willOpen = sub.hidden;
        sub.hidden = !willOpen;
        btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });
    });
  }
})();

/* ── Cart count badges: subscribe to cart changes ──
 * Any element carrying data-cart-count is kept in sync with the cart:
 * whoever mutates the cart dispatches
 *   document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount } }))
 * and every subscriber updates itself (text + hidden at zero). */
(function () {
  document.addEventListener('cart:updated', (e) => {
    if (!e.detail || typeof e.detail.itemCount !== 'number') return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = e.detail.itemCount;
      el.hidden = e.detail.itemCount === 0;
    });
  });
})();

/* ── Icon-style search: expand a band over the header row ── */
(function () {
  document.querySelectorAll('[data-search-expandable]').forEach((root) => {
    const openBtn = root.querySelector('[data-search-open]');
    const band = root.querySelector('[data-search-band]');
    if (!openBtn || !band) return;
    const input = root.querySelector('[data-predictive-input]');

    const openBand = () => {
      band.hidden = false;
      openBtn.setAttribute('aria-expanded', 'true');
      if (input) input.focus();
    };
    const closeBand = () => {
      band.hidden = true;
      openBtn.setAttribute('aria-expanded', 'false');
    };

    openBtn.addEventListener('click', openBand);
    root.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      closeBand();
      openBtn.focus();
    });
    document.addEventListener('click', (e) => {
      if (!root.contains(e.target)) closeBand();
    });
  });
})();

/* ── Predictive search ── */
(function () {
  document.querySelectorAll('[data-predictive-search]').forEach((root) => {
    const input = root.querySelector('[data-predictive-input]');
    const panel = root.querySelector('[data-predictive-results]');
    if (!input || !panel) return;

    const clearBtn = root.querySelector('[data-predictive-clear]');
    const syncClear = () => {
      if (clearBtn) clearBtn.hidden = input.value.trim().length === 0;
    };

    let controller;
    let debounce;

    const close = () => {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    };
    const showIfContent = () => {
      if (panel.innerHTML.trim()) {
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
    };

    const run = (q) => {
      if (q.length < 2) { panel.innerHTML = ''; close(); return; }
      if (controller) controller.abort();
      controller = new AbortController();
      const url = '/search/suggest?q=' + encodeURIComponent(q) +
        '&section_id=predictive-search&resources[type]=product,query&resources[limit]=6';
      fetch(url, { signal: controller.signal })
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('.ax-psearch');
          panel.innerHTML = content ? content.outerHTML : '';
          showIfContent();
        })
        .catch(() => {});
    };

    input.addEventListener('input', () => {
      syncClear();
      clearTimeout(debounce);
      debounce = setTimeout(() => run(input.value.trim()), 200);
    });
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2) showIfContent();
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        if (controller) controller.abort();
        clearTimeout(debounce);
        input.value = '';
        panel.innerHTML = '';
        close();
        syncClear();
        input.focus();
      });
    }
    syncClear();

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { close(); return; }
      const options = Array.from(panel.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      let idx = options.findIndex((o) => o.classList.contains('is-active'));
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        idx = idx + 1 >= options.length ? 0 : idx + 1;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        idx = idx <= 0 ? options.length - 1 : idx - 1;
      } else if (e.key === 'Enter') {
        if (idx >= 0) { e.preventDefault(); options[idx].click(); }
        return;
      } else {
        return;
      }
      options.forEach((o) => o.classList.remove('is-active'));
      const active = options[idx];
      active.classList.add('is-active');
      active.scrollIntoView({ block: 'nearest' });
      if (active.id) input.setAttribute('aria-activedescendant', active.id);
    });

    document.addEventListener('click', (e) => {
      if (!root.contains(e.target)) close();
    });
  });
})();
